import { Dialog, DialogBackdrop, DialogPanel, DialogTitle, Description } from '@headlessui/react'
import { useTranslation } from 'react-i18next'
import { ShieldCheckIcon } from '@heroicons/react/24/outline'
import { useQuery } from '@tanstack/react-query'
import CodeForm from './CodeForm'
import PasskeyPrompt from './PasskeyPrompt'
import { authAPI } from '../../services/api'
import { passkeysSupported } from '../../services/passkeys'
import { SECURITY_QUERY_KEY } from './securityQueries'

/**
 * VH-285 — "enter your code", shown before a sensitive action. It only
 * confirms: the action belongs to whoever opened it (`withConfirmation`).
 * Closing it confirms nothing. The form exists only while open, so a code
 * typed for one action is never still in the field for the next.
 *
 * VH-296: a user with a passkey here can confirm with it instead; the code
 * form stays below it. Whether there is one comes from the Security overview.
 */
export default function ConfirmCodeDialog({ open, onConfirmed, onCancel }) {
  const { t } = useTranslation()
  const supported = passkeysSupported()
  const overview = useQuery({
    queryKey: SECURITY_QUERY_KEY,
    queryFn: () => authAPI.getSecurity().then((response) => response.data),
    enabled: open && supported,
  })
  const canPasskey = supported && Number(overview.data?.passkeys) > 0
  return (
    <Dialog open={open} onClose={onCancel} className="relative z-50">
      <DialogBackdrop className="fixed inset-0 bg-gray-900/50" />
      <div className="fixed inset-0 flex items-end justify-center p-4 sm:items-center">
        <DialogPanel className="max-h-full w-full max-w-sm space-y-4 overflow-y-auto rounded-lg bg-surface-container p-5 shadow-xl">
          <DialogTitle className="flex items-center gap-2 text-base font-semibold text-gray-900">
            <ShieldCheckIcon className="h-5 w-5 flex-shrink-0 text-primary-600" />
            {t('secondFactor.confirm.title')}
          </DialogTitle>
          <Description className="text-sm text-gray-700">{t('secondFactor.confirm.intro')}</Description>
          {open && canPasskey && (
            <>
              <PasskeyPrompt
                idPrefix="confirm"
                ask={() => authAPI.confirmPasskeyOptions()}
                send={(credential) => authAPI.confirmPasskey(credential)}
                onDone={() => onConfirmed()}
              />
              <p className="text-center text-xs text-gray-500">{t('secondFactor.confirm.orCode')}</p>
            </>
          )}
          {open && (
            <CodeForm
              idPrefix="confirm"
              send={(typed) => authAPI.confirm(typed)}
              onDone={() => onConfirmed()}
              submitLabel={t('secondFactor.confirm.submit')}
            >
              <button type="button" onClick={onCancel} className="btn-secondary w-full sm:flex-1">
                {t('common.cancel')}
              </button>
            </CodeForm>
          )}
        </DialogPanel>
      </div>
    </Dialog>
  )
}
